import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { AppDispatch, RootState } from "../store/store";
import { loadMoreProviderResults } from "../store/slice/provider/providerThunks";
import LoadMoreButton from "./LoadMoreButton";
import { ProviderResult } from "../types/providerTypes";

const ProviderResultsTable: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { results, resultsPageInfo, loading, error } = useSelector(
    (state: RootState) => state.provider
  );

  // Only replace the table while loading a fresh search, not while loading more
  if (loading && results.length === 0) {
    return <p className="text-center mt-6">Searching providers...</p>;
  }

  if (error) {
    return <p className="text-center text-red-600 mt-6">{error}</p>;
  }

  if (!results || results.length === 0) {
    return (
      <p className="text-center text-gray-500 mt-6">
        No providers found for this search.
      </p>
    );
  }

  return (
    <div className="mt-8 bg-white shadow-lg rounded-lg overflow-x-auto">
      <table className="min-w-full border border-gray-200">
        <thead className="bg-gray-100">
          <tr>
            <th className="px-4 py-3 text-left text-sm font-semibold">
              Business Name
            </th>
            <th className="px-4 py-3 text-left text-sm font-semibold">
              Service
            </th>
            <th className="px-4 py-3 text-left text-sm font-semibold">
              Address
            </th>
            <th className="px-4 py-3 text-left text-sm font-semibold">Phone</th>
            <th className="px-4 py-3 text-center text-sm font-semibold">
              Price
            </th>
            <th className="px-4 py-3 text-center text-sm font-semibold">
              Rating
            </th>
            <th className="px-4 py-3 text-center text-sm font-semibold">
              Distance
            </th>
            <th className="px-4 py-3 text-center text-sm font-semibold" />
          </tr>
        </thead>

        <tbody>
          {results.map((p: ProviderResult) => (
            <tr key={p.id} className="border-t hover:bg-gray-50">
              <td className="px-4 py-3 text-sm font-medium">
                <div className="flex items-center gap-2">
                  <span>{p.businessName || p.fullName}</span>
                  {p.verificationStatus === "VERIFIED" && (
                    <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                      ✓ Verified
                    </span>
                  )}
                </div>
              </td>

              <td className="px-4 py-3 text-sm">{p.serviceName}</td>

              <td className="px-4 py-3 text-sm">{p.businessAddress}</td>

              <td className="px-4 py-3 text-sm">
                <a
                  href={`tel:${p.phone}`}
                  className="text-green-600 hover:underline"
                >
                  {p.phone}
                </a>
              </td>

              <td className="px-4 py-3 text-center text-sm">
                {p.price ? `₦${p.price}` : "—"}
              </td>

              <td className="px-4 py-3 text-center text-sm">
                ⭐ {p.rating?.toFixed(1)}
              </td>

              <td className="px-4 py-3 text-center text-sm">
                {p.distance ? `${p.distance} km` : "—"}
              </td>

              <td className="px-4 py-3 text-center text-sm">
                <Link
                  to={`/provider/${p.id}`}
                  className="text-green-600 hover:underline"
                >
                  View Profile
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="pb-4">
        <LoadMoreButton
          shown={results.length}
          total={resultsPageInfo.totalElements}
          last={resultsPageInfo.last}
          loading={loading}
          onClick={() => dispatch(loadMoreProviderResults())}
        />
      </div>
    </div>
  );
};

export default ProviderResultsTable;
